import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccount } from "wagmi";
import { Modal } from "./Modal";
import { MktButton, Stat } from "./primitives";
import { symbolFor } from "./mkt-format";
import { mktError } from "./mkt-errors";
import { useArtworks, useAuctions, useMarketplaceConfig } from "./useMarketplace";
import { useSession } from "./useSession";
import { useCreateAuction } from "./auction-onchain";
import { useT } from "../../i18n";
import { marketplaceApi, MarketplaceError, type ArtworkSummary, type MarketplaceConfig } from "../../lib/marketplace-api";

export interface SellArtworkTarget {
  artwork_id: string;
  title: string;
  royalty_percent: number;
  footprint: number;
  width: number;
  height: number;
}

type Step = "pick" | "mode" | "fixed" | "auction" | "done";
type Mode = "fixed" | "auction";

function errText(t: ReturnType<typeof useT>, e: unknown): { title: string; hint: string } {
  if (e instanceof MarketplaceError) return mktError(t, e.code, e.message);
  return mktError(t, undefined, e instanceof Error ? e.message : undefined);
}

/** Frozen, placed artworks the session wallet owns that are NOT already listed/auctioned. */
export function sellableArtworks(all: ArtworkSummary[], wallet: string | null): ArtworkSummary[] {
  const w = (wallet ?? "").toLowerCase();
  if (!w) return [];
  return all.filter(
    (a) => a.owner.toLowerCase() === w && a.placement != null && !a.auction && !a.fixed_price,
  );
}

export function SellArtworkModal({
  open, target, onClose, onDone,
}: {
  open: boolean;
  target?: SellArtworkTarget | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const { data: config } = useMarketplaceConfig();
  const session = useSession();
  const artworksQuery = useArtworks();
  const [chosen, setChosen] = useState<SellArtworkTarget | null>(target ?? null);
  const [step, setStep] = useState<Step>(target ? "mode" : "pick");
  const [mode, setMode] = useState<Mode>("fixed");

  const options = useMemo(
    () => sellableArtworks(artworksQuery.data?.artworks ?? [], session.wallet),
    [artworksQuery.data, session.wallet],
  );

  const close = onClose;

  if (!open) return null;

  return (
    <Modal open onClose={close} size="lg"
      title={t("marketplace.artworkMkt.wizardTitle")}
      subtitle={chosen?.title ?? undefined}
      testId="sell-artwork-modal">
      {step === "pick" && (
        <PickStep options={options} loading={artworksQuery.isLoading}
          onPick={(a) => { setChosen(a); setStep("mode"); }} />
      )}
      {step === "mode" && chosen && (
        <ModeStep artwork={chosen} mode={mode} setMode={setMode}
          onBack={target ? undefined : () => setStep("pick")}
          onNext={() => setStep(mode)} />
      )}
      {step === "fixed" && chosen && (
        <FixedStep artwork={chosen} config={config}
          onBack={() => setStep("mode")} onDone={() => { setStep("done"); onDone(); }} />
      )}
      {step === "auction" && chosen && (
        <AuctionStep artwork={chosen} config={config} session={session}
          onBack={() => setStep("mode")} onDone={() => { setStep("done"); onDone(); }} />
      )}
      {step === "done" && chosen && (
        <DoneStep mode={mode} onClose={close} />
      )}
    </Modal>
  );
}

function PickStep({ options, loading, onPick }: {
  options: ArtworkSummary[]; loading: boolean; onPick: (a: ArtworkSummary) => void;
}) {
  const t = useT();
  return (
    <div data-testid="sell-artwork-pick">
      <h4 className="font-display text-base text-text-primary">{t("marketplace.artworkMkt.pickTitle")}</h4>
      <p className="mt-1 font-mono text-[11px] text-text-muted leading-relaxed">{t("marketplace.artworkMkt.pickHint")}</p>
      {loading ? (
        <p className="mt-6 font-mono text-[11px] text-text-muted animate-pulse">…</p>
      ) : options.length === 0 ? (
        <div data-testid="sell-artwork-pick-empty" className="mt-6 neon-glass rounded-lg border border-border-divider p-6 text-center">
          <p className="font-display text-sm text-text-secondary">{t("marketplace.artworkMkt.pickEmpty")}</p>
          <p className="mt-2 font-mono text-[10.5px] text-text-muted">{t("marketplace.artworkMkt.pickEmptyHint")}</p>
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {options.map((a) => (
            <button key={a.artwork_id} type="button" data-testid="sell-artwork-pick-option"
              data-artwork-id={a.artwork_id} onClick={() => onPick(a)}
              className="text-left neon-glass rounded-lg border border-border-divider p-4 hover:border-neon-teal/50 hover:shadow-glow-teal-sm transition-[border-color,box-shadow]">
              <span className="font-display text-sm text-text-primary block truncate">{a.title}</span>
              <span className="mt-1 font-mono text-[10px] text-text-muted block">{a.width}×{a.height} · {a.footprint} {t("marketplace.detail.cells").toLowerCase()} · {a.royalty_percent}%</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ModeStep({ artwork, mode, setMode, onBack, onNext }: {
  artwork: SellArtworkTarget; mode: Mode; setMode: (m: Mode) => void; onBack?: () => void; onNext: () => void;
}) {
  const t = useT();
  const cards: { key: Mode; title: string; desc: string }[] = [
    { key: "fixed", title: t("marketplace.artworkMkt.modeFixed"), desc: t("marketplace.artworkMkt.modeFixedDesc") },
    { key: "auction", title: t("marketplace.artworkMkt.modeAuction"), desc: t("marketplace.artworkMkt.modeAuctionDesc") },
  ];
  return (
    <div data-testid="sell-artwork-mode">
      <h4 className="font-display text-base text-text-primary">{t("marketplace.artworkMkt.chooseMode")}</h4>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {cards.map((c) => (
          <button key={c.key} type="button" data-testid={`sell-artwork-mode-${c.key}`}
            data-active={mode === c.key ? "true" : "false"} onClick={() => setMode(c.key)}
            className={`text-left neon-glass rounded-lg border p-4 transition-[border-color,box-shadow] ${mode === c.key ? "border-neon-teal/60 shadow-glow-teal-sm" : "border-border-divider hover:border-border-strong"}`}>
            <span className="font-display text-sm text-text-primary block">{c.title}</span>
            <span className="mt-1.5 font-mono text-[10px] text-text-muted leading-relaxed block">{c.desc}</span>
          </button>
        ))}
      </div>
      <div className="mt-6 grid grid-cols-4 gap-3 neon-glass rounded-lg border border-border-divider p-4">
        <Stat label={t("marketplace.artworkMkt.reviewArtwork")}>{artwork.title}</Stat>
        <Stat label={t("marketplace.detail.cells")}>{artwork.footprint}</Stat>
        <Stat label={t("marketplace.artworkMkt.reviewRoyalty")}>{artwork.royalty_percent}%</Stat>
        <Stat label={t("marketplace.detail.size")}>{artwork.width}×{artwork.height}</Stat>
      </div>
      <div className="mt-6 flex gap-3">
        {onBack && <MktButton variant="ghost" className="flex-1" testId="sell-artwork-mode-back" onClick={onBack}>{t("marketplace.artworkMkt.back")}</MktButton>}
        <MktButton variant="primary" className="flex-1" testId="sell-artwork-mode-next" onClick={onNext}>{t("marketplace.artworkMkt.next")}</MktButton>
      </div>
    </div>
  );
}

function ErrBox({ err }: { err: { title: string; hint: string } | null }) {
  if (!err) return null;
  return (
    <div data-testid="sell-artwork-error" className="mt-4 rounded-lg border border-state-danger/40 bg-state-danger/10 p-4">
      <p className="font-display text-sm text-state-danger">{err.title}</p>
      <p className="mt-1.5 font-mono text-[10.5px] text-text-muted leading-relaxed">{err.hint}</p>
    </div>
  );
}

function FixedStep({ artwork, config, onBack, onDone }: {
  artwork: SellArtworkTarget; config?: MarketplaceConfig | null; onBack: () => void; onDone: () => void;
}) {
  const t = useT();
  const qc = useQueryClient();
  const currencies = (config?.currencies ?? []).filter((c) => c.enabled && c.address);
  const [currency, setCurrency] = useState(currencies[0]?.address ?? "");
  const [amount, setAmount] = useState("");
  const [err, setErr] = useState<{ title: string; hint: string } | null>(null);
  const sym = symbolFor(currency, config);
  const micro = Math.round(Number(amount) * 1_000_000);
  const feePct = ((config?.grid_fee_bps ?? 250) / 100).toFixed(2);

  const mut = useMutation({
    mutationFn: () => marketplaceApi.listArtwork(artwork.artwork_id, { currency: currency.toLowerCase(), price_micro: micro }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mkt", "artworks"] });
      qc.invalidateQueries({ queryKey: ["mkt", "artwork", artwork.artwork_id] });
      qc.invalidateQueries({ queryKey: ["mkt", "mine"] });
      onDone();
    },
    onError: (e) => setErr(errText(t, e)),
  });

  const canSubmit = micro > 0 && !!currency && !mut.isPending;
  return (
    <div data-testid="sell-artwork-fixed">
      <h4 className="font-display text-base text-text-primary">{t("marketplace.artworkMkt.setPrice")}</h4>
      <p className="mt-1 font-mono text-[10.5px] text-text-muted leading-relaxed">
        {t("marketplace.artworkMkt.priceHint", { sym, fee: feePct, roy: artwork.royalty_percent })}
      </p>
      <label className="mt-5 block">
        <span className="font-mono text-[9.5px] tracking-[0.2em] uppercase text-text-muted mb-1.5 block">{t("marketplace.artworkMkt.priceLabel")}</span>
        <div className="flex items-center gap-2">
          <input data-testid="sell-artwork-price-input" type="number" inputMode="decimal" min="0" placeholder="0.00"
            value={amount} onChange={(e) => setAmount(e.target.value)}
            className="w-full h-11 px-3 rounded-md bg-elevated/60 border border-border-divider text-text-primary font-display text-lg focus:outline-none focus:border-neon-teal/60" />
          <select data-testid="sell-artwork-currency" value={currency} onChange={(e) => setCurrency(e.target.value)}
            className="h-11 px-2 rounded-md bg-[#0E141B] border border-border-divider text-text-primary font-mono text-[11px] focus:outline-none focus:border-neon-teal/60 appearance-none">
            {currencies.map((c) => <option key={c.symbol} value={c.address ?? ""}>{c.symbol}</option>)}
          </select>
        </div>
      </label>
      <ErrBox err={err} />
      <div className="mt-6 flex gap-3">
        <MktButton variant="ghost" className="flex-1" testId="sell-artwork-fixed-back" onClick={onBack} disabled={mut.isPending}>{t("marketplace.artworkMkt.back")}</MktButton>
        <MktButton variant="primary" className="flex-1" testId="sell-artwork-fixed-submit" onClick={() => { setErr(null); mut.mutate(); }} disabled={!canSubmit}>
          {mut.isPending ? t("marketplace.artworkMkt.listing") : t("marketplace.artworkMkt.listBtn")}
        </MktButton>
      </div>
    </div>
  );
}

const DURATIONS: { s: number; key: string }[] = [
  { s: 3600, key: "dur1h" }, { s: 6 * 3600, key: "dur6h" }, { s: 24 * 3600, key: "dur24h" },
  { s: 3 * 24 * 3600, key: "dur3d" }, { s: 7 * 24 * 3600, key: "dur7d" },
];
const ANTISNIPE: { s: number; key: string }[] = [
  { s: 60, key: "antiSnipe60" }, { s: 300, key: "antiSnipe300" }, { s: 0, key: "antiSnipeOff" },
];

function AuctionStep({ artwork, config, session, onBack, onDone }: {
  artwork: SellArtworkTarget; config?: MarketplaceConfig | null; session: ReturnType<typeof useSession>;
  onBack: () => void; onDone: () => void;
}) {
  const t = useT();
  const auctionsQuery = useAuctions();
  const { address } = useAccount();
  const { state, create, isPending } = useCreateAuction();
  const currencies = (config?.currencies ?? []).filter((c) => c.enabled && c.address);
  const [currency, setCurrency] = useState(currencies[0]?.address ?? "");
  const [reserve, setReserve] = useState("");
  const [increment, setIncrement] = useState("1");
  const [duration, setDuration] = useState(24 * 3600);
  const [antiSnipe, setAntiSnipe] = useState(60);
  const [err, setErr] = useState<{ title: string; hint: string } | null>(null);
  const sym = symbolFor(currency, config);

  const reserveMicro = Math.round(Number(reserve) * 1_000_000);
  const incrementMicro = Math.round(Number(increment) * 1_000_000);
  const escrow = config?.auction_escrow ?? null;
  const walletMatch = !!address && !!session.wallet && address.toLowerCase() === session.wallet;

  const submit = async () => {
    setErr(null);
    if (!escrow || !config) { setErr({ title: t("marketplace.errors.disabledTitle"), hint: t("marketplace.errors.disabledHint") }); return; }
    const now = auctionsQuery.data?.market_now ?? Math.floor(Date.now() / 1000);
    try {
      await create({
        body: {
          artwork_id: artwork.artwork_id, currency: currency.toLowerCase(),
          reserve_micro: reserveMicro, min_increment_micro: incrementMicro,
          start_market_ts: now, end_market_ts: now + duration,
          anti_snipe_window: antiSnipe, nonce: Date.now(),
        },
        escrow: escrow as `0x${string}`, chainId: config.chain_id,
      });
    } catch (e) { setErr(errText(t, e)); }
  };

  // React to the create hook's terminal state.
  useEffect(() => {
    if (state.kind === "completed") onDone();
  }, [state.kind, onDone]);

  const busyLabel = state.kind === "preparing" ? t("marketplace.artworkMkt.creatingAuction")
    : state.kind === "awaiting_wallet" ? t("marketplace.artworkMkt.auctionWalletConfirm")
    : state.kind === "submitting" ? t("marketplace.artworkMkt.auctionSubmitting")
    : null;
  const stateErr = state.kind === "error" ? { title: t("marketplace.artworkMkt.buyFailed"), hint: state.error }
    : state.kind === "reverted" ? { title: t("marketplace.artworkMkt.buyFailed"), hint: t("marketplace.artworkMkt.buyReverted") }
    : state.kind === "rejected" ? { title: t("marketplace.artworkMkt.buyFailed"), hint: t("marketplace.artworkMkt.buyRejected") }
    : null;

  const canSubmit = reserveMicro > 0 && incrementMicro > 0 && !!currency && !!escrow && walletMatch && !isPending;
  return (
    <div data-testid="sell-artwork-auction">
      <h4 className="font-display text-base text-text-primary">{t("marketplace.artworkMkt.setAuction")}</h4>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="font-mono text-[9.5px] tracking-[0.2em] uppercase text-text-muted mb-1.5 block">{t("marketplace.artworkMkt.reserveLabel")} ({sym})</span>
          <div className="flex items-center gap-2">
            <input data-testid="sell-artwork-reserve" type="number" inputMode="decimal" min="0" placeholder="0.00" value={reserve}
              onChange={(e) => setReserve(e.target.value)}
              className="w-full h-10 px-3 rounded-md bg-elevated/60 border border-border-divider text-text-primary font-display text-base focus:outline-none focus:border-neon-teal/60" />
            <select data-testid="sell-artwork-auction-currency" value={currency} onChange={(e) => setCurrency(e.target.value)}
              className="h-10 px-2 rounded-md bg-[#0E141B] border border-border-divider text-text-primary font-mono text-[11px] focus:outline-none focus:border-neon-teal/60 appearance-none">
              {currencies.map((c) => <option key={c.symbol} value={c.address ?? ""}>{c.symbol}</option>)}
            </select>
          </div>
        </label>
        <label className="block">
          <span className="font-mono text-[9.5px] tracking-[0.2em] uppercase text-text-muted mb-1.5 block">{t("marketplace.artworkMkt.minIncrementLabel")} ({sym})</span>
          <input data-testid="sell-artwork-increment" type="number" inputMode="decimal" min="0" value={increment}
            onChange={(e) => setIncrement(e.target.value)}
            className="w-full h-10 px-3 rounded-md bg-elevated/60 border border-border-divider text-text-primary font-display text-base focus:outline-none focus:border-neon-teal/60" />
        </label>
        <label className="block">
          <span className="font-mono text-[9.5px] tracking-[0.2em] uppercase text-text-muted mb-1.5 block">{t("marketplace.artworkMkt.durationLabel")}</span>
          <select data-testid="sell-artwork-duration" value={duration} onChange={(e) => setDuration(Number(e.target.value))}
            className="w-full h-10 px-3 rounded-md bg-[#0E141B] border border-border-divider text-text-primary font-mono text-[11px] focus:outline-none focus:border-neon-teal/60 appearance-none">
            {DURATIONS.map((d) => <option key={d.s} value={d.s}>{t(`marketplace.artworkMkt.${d.key}`)}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="font-mono text-[9.5px] tracking-[0.2em] uppercase text-text-muted mb-1.5 block">{t("marketplace.artworkMkt.antiSnipeLabel")}</span>
          <select data-testid="sell-artwork-antisnipe" value={antiSnipe} onChange={(e) => setAntiSnipe(Number(e.target.value))}
            className="w-full h-10 px-3 rounded-md bg-[#0E141B] border border-border-divider text-text-primary font-mono text-[11px] focus:outline-none focus:border-neon-teal/60 appearance-none">
            {ANTISNIPE.map((a) => <option key={a.key} value={a.s}>{t(`marketplace.artworkMkt.${a.key}`)}</option>)}
          </select>
        </label>
      </div>
      {!walletMatch && (
        <p data-testid="sell-artwork-auction-wallet" className="mt-4 font-mono text-[10.5px] text-state-warning">
          {t("marketplace.sell.wholeConnectRequired")}
        </p>
      )}
      {busyLabel && <p data-testid="sell-artwork-auction-busy" className="mt-4 font-mono text-[11px] text-neon-teal">{busyLabel}</p>}
      <ErrBox err={err ?? stateErr} />
      <div className="mt-6 flex gap-3">
        <MktButton variant="ghost" className="flex-1" testId="sell-artwork-auction-back" onClick={onBack} disabled={isPending}>{t("marketplace.artworkMkt.back")}</MktButton>
        <MktButton variant="coral" className="flex-1" testId="sell-artwork-auction-submit" onClick={submit} disabled={!canSubmit}>
          {isPending ? t("marketplace.artworkMkt.creatingAuction") : t("marketplace.artworkMkt.createAuctionBtn")}
        </MktButton>
      </div>
    </div>
  );
}

function DoneStep({ mode, onClose }: { mode: Mode; onClose: () => void }) {
  const t = useT();
  return (
    <div data-testid="sell-artwork-done" className="text-center py-6">
      <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-neon-teal/15 border border-neon-teal/40 flex items-center justify-center">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden><path d="M5 10l3 3 7-7" stroke="#37E0C8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </div>
      <p className="font-display text-base text-neon-teal">{mode === "fixed" ? t("marketplace.artworkMkt.listedTitle") : t("marketplace.artworkMkt.auctionCreatedTitle")}</p>
      <p className="mt-2 font-mono text-[11px] text-text-muted max-w-sm mx-auto leading-relaxed">
        {mode === "fixed" ? t("marketplace.artworkMkt.listedBody") : t("marketplace.artworkMkt.auctionCreatedBody")}
      </p>
      <div className="mt-6">
        <MktButton variant="primary" testId="sell-artwork-done-close" onClick={onClose}>{t("marketplace.artworkMkt.done")}</MktButton>
      </div>
    </div>
  );
}
